import { formatNumber } from "@/v2-common/utils/utils.formatting"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { UsageAppMixEntry } from "@/v2-backoffice-client/usage/UsageAppMix"
import {
  formatAppArea,
  formatAppCode,
  isMcpAppCode,
  formatMunicipalityName,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  CHURN_RISK_LABELS,
  type UsageScopeSummary,
} from "@/v2-backoffice-client/usage/scope/usage.scope.utils"
import {
  describeTrend,
  formatShare,
  list,
} from "@/v2-backoffice-client/usage/usage.text"

/** "A, B and C" */

/**
 * The Overview's opening paragraphs for a customer or team, written from
 * the scope summary and the product mix: seats and activity, what the work
 * is about, which products carry it, and the churn read with the money at
 * stake. Plain sentences a CS colleague can read aloud in a call.
 */
export function deriveScopeStory(input: {
  summary: UsageScopeSummary
  appMix: UsageAppMixEntry[]
  subject: string
  revenueAtStake?: { amount: number; currency: string | null } | null
}): string[] {
  const { summary, appMix, subject, revenueAtStake } = input
  const paragraphs: string[] = []

  if (summary.seatCount === 0) return paragraphs

  // Seats and activity.
  {
    const sentences: string[] = []
    const activity = Math.round(summary.weightedEvents28d ?? summary.events28d)
    sentences.push(
      `${summary.activeUsers28d} of ${subject}'s ${summary.seatCount} seat${summary.seatCount === 1 ? "" : "s"} ${summary.activeUsers28d === 1 ? "was" : "were"} in use in the last 28 days${
        summary.activeUsers7d > 0
          ? `, ${summary.activeUsers7d} in the last week`
          : ""
      }.`,
    )
    if (activity > 0) {
      sentences.push(
        `Together they did ${formatNumber(activity)} weighted actions${
          summary.trendPct != null
            ? `, ${describeTrend(summary.trendPct)} on the four weeks before`
            : ""
        }.`,
      )
    }
    if (summary.avgScore != null) {
      sentences.push(
        `Engagement among the active seats scores ${Math.round(summary.avgScore)} of 100.`,
      )
    }
    paragraphs.push(sentences.join(" "))
  }

  // What the work is about, and through which products.
  {
    const sentences: string[] = []
    const areas = summary.topAreas.slice(0, 3)
    if (areas.length > 0) {
      sentences.push(
        `The work is mostly ${list(areas.map((area) => formatAppArea(area.area)))}${
          summary.topGeoAreas[0]
            ? `, concentrated in ${list(
                summary.topGeoAreas
                  .slice(0, 2)
                  .map((geo) => formatMunicipalityName(geo.Name)),
              )}`
            : ""
        }.`,
      )
    }
    const total = appMix.reduce((sum, entry) => sum + entry.weighted28d, 0)
    const mcp = appMix.filter(
      (entry) => isMcpAppCode(entry.app) && entry.weighted28d > 0,
    )
    const v1 = appMix.find((entry) => entry.app === "v1-console")
    if (total > 0 && mcp.length > 0) {
      const mcpShare = Math.round(
        (mcp.reduce((sum, entry) => sum + entry.weighted28d, 0) * 100) / total,
      )
      const seats = Math.max(...mcp.map((entry) => entry.activeSeats ?? 0))
      sentences.push(
        `${formatShare(mcpShare)} of the activity now runs through ${list(mcp.map((entry) => formatAppCode(entry.app)))} from an AI client${
          seats > 0 ? ` (${seats} ${seats === 1 ? "person" : "people"})` : ""
        }.`,
      )
    }
    if (v1 && total > 0 && v1.weighted28d > 0) {
      sentences.push(
        `${formatShare(Math.round((v1.weighted28d * 100) / total))} still happens in Console v1.`,
      )
    }
    if (
      summary.untouchedAreas.length > 0 &&
      summary.untouchedAreas.length <= 4
    ) {
      sentences.push(
        `Nobody has touched ${list(summary.untouchedAreas.map((area) => formatAppArea(area)))}.`,
      )
    }
    if (sentences.length > 0) paragraphs.push(sentences.join(" "))
  }

  // The churn read.
  {
    const level = summary.churnRisk.level
    if (level === "high" || level === "medium" || level === "low") {
      const sentences: string[] = [
        `Churn risk reads "${CHURN_RISK_LABELS[level]}"${
          summary.churnRisk.reasons.length > 0
            ? `: ${summary.churnRisk.reasons
                .map((reason) => reason.replace(/\.$/, ""))
                .join("; ")
                .toLowerCase()}`
            : ""
        }.`,
      ]
      if (
        revenueAtStake &&
        revenueAtStake.amount > 0 &&
        (level === "high" || level === "medium")
      ) {
        sentences.push(
          `${formatInvoiceAmount(revenueAtStake.amount, revenueAtStake.currency)} billed over the last twelve months rides on this account.`,
        )
      }
      paragraphs.push(sentences.join(" "))
    }
  }

  return paragraphs
}
